import {
  Apollo,
  Dollar,
  Form,
  HubSpot,
  PandaDoc,
  Slack,
  Zoom,
  ZoomInfo
} from "@dreamhub/designsystem/icons";
import { Service } from "@/pages/settings/components/connectedAccounts/types";
import apiRoutes from "@/services/api/routes";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useMemo } from "react";

export const INTEGRATIONS_TRANSLATION_PREFIX = "integrations";
export const SETUP_TOAST_DURATION = 3000;

interface BaseIntegration {
  id: string;
  icon: typeof Zoom;
}

// Connected through a Dreamhub service token and the backend register route.
export interface TokenIntegration extends BaseIntegration {
  kind: "token";
  serviceId: Service;
  registerTo: string;
}

// Connected by an API key the admin pastes from the vendor's own settings.
export interface ApiKeyIntegration extends BaseIntegration {
  kind: "apiKey";
  serviceId: Service;
  connectTo: keyof typeof apiRoutes;
  keyPageUrl: string;
}

// Installed through Ampersand, where the integration name is the id.
export interface AmpersandIntegration extends BaseIntegration {
  kind: "ampersand";
}

// Dreamhub's own integration, set up entirely inside Dreamhub with no third party.
export interface NativeIntegration extends BaseIntegration {
  kind: "native";
}

export type ServiceTokenIntegration = TokenIntegration | ApiKeyIntegration;

export type Integration =
  | ServiceTokenIntegration
  | AmpersandIntegration
  | NativeIntegration;

// Token and API-key integrations both report their connection through a service token.
export const hasServiceToken = (
  integration: Integration
): integration is ServiceTokenIntegration =>
  integration.kind === "token" || integration.kind === "apiKey";

// Only account-level services with a working connect and disconnect flow are
// listed here; each new integration is added as its own entry.
export const INTEGRATIONS: Integration[] = [
  {
    kind: "token",
    id: "zoom",
    serviceId: Service.ZOOM,
    icon: Zoom,
    registerTo: "registerZoom"
  },
  {
    kind: "token",
    id: "slack",
    serviceId: Service.SLACK,
    icon: Slack,
    registerTo: "registerSlack"
  },
  {
    kind: "token",
    id: "zoominfo",
    serviceId: Service.ZOOMINFO,
    icon: ZoomInfo,
    registerTo: "registerZoomInfo"
  },
  {
    kind: "ampersand",
    id: "apollo",
    icon: Apollo
  },
  {
    kind: "ampersand",
    id: "hubspot",
    icon: HubSpot
  },
  {
    kind: "apiKey",
    id: "pandadoc",
    serviceId: Service.PANDADOC,
    icon: PandaDoc,
    connectTo: "connectPandaDoc",
    keyPageUrl: "https://app.pandadoc.com/a/#/api-dashboard/configuration"
  },
  {
    kind: "native",
    id: "webForm",
    icon: Form
  },
  {
    kind: "native",
    id: "stripeCheckout",
    icon: Dollar
  }
];

const useIntegrationsWithLoading = () => {
  const { enabled: showZoomInfo, loading: loadingZoomInfo } = useFeatureFlag(
    FeatureFlags.CRM_9112_ZOOMINFO_INTEGRATION
  );
  const { enabled: showPandaDoc, loading: loadingPandaDoc } = useFeatureFlag(
    FeatureFlags.CRM_9761_CPQ
  );
  const { enabled: showHubSpot, loading: loadingHubSpot } = useFeatureFlag(
    FeatureFlags.CRM_11069_HUBSPOT_SETTINGS
  );
  const { enabled: showWebForm, loading: loadingWebForm } = useFeatureFlag(
    FeatureFlags.CRM_11179_WEB_FORM_SETTINGS
  );
  const { enabled: showStripeCheckout, loading: loadingStripeCheckout } =
    useFeatureFlag(FeatureFlags.CRM_10479_STRIPE_CHECKOUT_SETTINGS);
  const integrations = useMemo(
    () =>
      INTEGRATIONS.filter(
        (integration) =>
          (showZoomInfo || integration.id !== "zoominfo") &&
          (showPandaDoc || integration.id !== "pandadoc") &&
          (showHubSpot || integration.id !== "hubspot") &&
          (showWebForm || integration.id !== "webForm") &&
          (showStripeCheckout || integration.id !== "stripeCheckout")
      ),
    [showZoomInfo, showPandaDoc, showHubSpot, showWebForm, showStripeCheckout]
  );
  // Each flagged integration waits only on its own flag.
  const isLoading = (id?: string) =>
    (id === "zoominfo" && loadingZoomInfo) ||
    (id === "pandadoc" && loadingPandaDoc) ||
    (id === "hubspot" && loadingHubSpot) ||
    (id === "webForm" && loadingWebForm) ||
    (id === "stripeCheckout" && loadingStripeCheckout);
  return { integrations, isLoading };
};

export const useIntegrations = (): Integration[] =>
  useIntegrationsWithLoading().integrations;

export const useIntegrationById = (id?: string) => {
  const { integrations, isLoading } = useIntegrationsWithLoading();
  return {
    integration: integrations.find((integration) => integration.id === id),
    // An ID that no flag gates resolves to not-found without waiting.
    loading: isLoading(id)
  };
};
